import {
  BayLcgVcgTcgAndPairings,
  IVesselOnePartWithCalcs,
} from "@tedivo/tedivo-bay-grid-pure";

import { bayHasLcgs, findReferenceLcg } from "./findReferenceLcg";

import { ForeAftEnum } from "open-vessel-definition";
import { findComposedRefId } from "./findComposedRefId";
import { getClosestVesselPart } from "./getClosestVesselPart";
import { sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

export function calculateSpaceAvailableNext({
  slotRefId,
  posRef,
  calculatedSlots,
  lcgVcgTcgAndPairings,
  includeLenOfId,
}: {
  slotRefId: string;
  posRef: ForeAftEnum;
  calculatedSlots: Array<IVesselOnePartWithCalcs>;
  lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings;
  includeLenOfId: string | undefined;
}) {
  if (lcgVcgTcgAndPairings.missingImportantLcgs) return Infinity;

  const composedRefId = findComposedRefId(slotRefId, calculatedSlots);

  // Empty bays have no LCGs, so they can't be used as limits
  const isBayUsable = (id: string) => bayHasLcgs(id, lcgVcgTcgAndPairings);

  // Go FWD or AFT looking for the next closest bay
  const closestNextBay = getClosestVesselPart({
    id: composedRefId,
    posRef,
    isSelf: false,
    calculatedSlots,
    isBayUsable,
  });

  if (!closestNextBay) return undefined;

  // Get the closest opposite bay
  const closestOppositeBay = getClosestVesselPart({
    id: composedRefId,
    posRef,
    isSelf: true, // Inverts direction
    calculatedSlots,
    isBayUsable,
  });

  if (!closestOppositeBay) return undefined;

  const bayIndexes = [
    closestNextBay.bayIndex,
    closestOppositeBay.bayIndex,
  ].sort(sortNumericAsc);

  const inBetweenPartsLen = calculatedSlots
    .filter(
      (p, idx) =>
        idx > bayIndexes[0] && idx < bayIndexes[1] && p.id !== includeLenOfId,
    )
    .map((p) => p.original?.len || 0)
    .reduce((a, b) => a + b, 0);

  // If we looked for the closest bay in one direction, get the LCG in the opposite direction
  const lcgRefDir = findReferenceLcg(
    closestNextBay.id,
    posRef === ForeAftEnum.AFT ? ForeAftEnum.FWD : ForeAftEnum.AFT,
    lcgVcgTcgAndPairings,
  );

  // The same as above, but for the opposite bay
  const lcgOppDir = findReferenceLcg(
    closestOppositeBay.id,
    posRef,
    lcgVcgTcgAndPairings,
  );

  const spaceAvailable = Math.abs(lcgOppDir - lcgRefDir) - inBetweenPartsLen;

  return Math.floor(spaceAvailable);
}
